import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Clock, User, FileText, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getStatusLabel as getCanonicalStatusLabel } from '../constants/statuses';

const API = process.env.REACT_APP_BACKEND_URL;

const HistoriqueModal = ({ open, onClose, operationId, operation = null }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && operationId) {
      fetchHistory();
    }
  }, [open, operationId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/operation-status/${operationId}/history`,
        {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        }
      );
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Construit un historique de secours à partir des timestamps connus
  // quand aucune entrée status_history n'existe (opérations importées / historiques).
  const fallbackEntries = React.useMemo(() => {
    if (!operation) return [];
    const entries = [];
    const push = (label, date, source = 'Timestamp système') => {
      if (!date) return;
      entries.push({
        label,
        date,
        source,
      });
    };
    push('Demande créée', operation.createdAt);
    push('Devis accepté', operation.quoteAcceptedAt);
    push('Planification', operation.plannedAt);
    push('Prêt au chargement', operation.readyForLoadingAt);
    push('Début chargement', operation.loadingStartedAt);
    push('Fin chargement / En route', operation.loadedAt);
    push('Livraison', operation.deliveredAt || operation.actualDeliveryDate);
    push('Facture générée', operation.invoiceGeneratedAt);
    push('Paiement reçu', operation.paymentReceivedAt);
    push('Dernière mise à jour', operation.updatedAt);
    return entries.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [operation]);

  const getStatusLabel = (status) => getCanonicalStatusLabel(status);

  const getStatusColor = (status) => {
    if (status.includes('annulee')) return 'bg-red-100 text-red-700 border-red-300';
    if (status === 'terminee') return 'bg-green-100 text-green-700 border-green-300';
    if (status === 'payee') return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    if (status.includes('attente')) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-blue-100 text-blue-700 border-blue-300';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-600" />
            Historique complet de l'opération
          </DialogTitle>
          <DialogDescription>
            Tous les changements de statut et actions effectuées
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[500px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : history.length === 0 ? (
            fallbackEntries.length > 0 ? (
              <div className="relative">
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  <strong>Historique détaillé non disponible</strong> — cette opération a été créée avant la mise en place du tracking complet. Voici les timestamps connus :
                </div>
                <div className="absolute left-6 top-20 bottom-6 w-0.5 bg-gray-200"></div>
                <div className="space-y-4">
                  {fallbackEntries.map((entry, idx) => (
                    <div key={idx} className="relative flex gap-4">
                      <div className="flex-shrink-0 w-12 flex justify-center">
                        <div className="w-3 h-3 rounded-full bg-gray-400 border-4 border-white shadow relative z-10"></div>
                      </div>
                      <div className="flex-1 bg-white border rounded-lg p-3 shadow-sm">
                        <div className="flex items-start justify-between">
                          <span className="font-medium text-sm text-gray-800">{entry.label}</span>
                          <span className="text-xs text-gray-500">{formatDate(entry.date)}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">{entry.source}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                <FileText className="w-12 h-12 mb-2 opacity-30" />
                <p className="text-sm">Aucun historique disponible</p>
              </div>
            )
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-gray-200"></div>

              {/* History items */}
              <div className="space-y-6">
                {history.map((item, index) => (
                  <div key={index} className="relative flex gap-4">
                    {/* Timeline dot */}
                    <div className="flex-shrink-0 w-12 flex justify-center">
                      <div className="w-3 h-3 rounded-full bg-emerald-500 border-4 border-white shadow relative z-10"></div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 bg-white border rounded-lg p-4 shadow-sm">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`${getStatusColor(item.old_status)} border`}>
                            {getStatusLabel(item.old_status)}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                          <Badge className={`${getStatusColor(item.new_status)} border`}>
                            {getStatusLabel(item.new_status)}
                          </Badge>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4" />
                          <span className="font-medium">{item.changed_by_name}</span>
                          <span className="text-gray-400">•</span>
                          <Badge variant="outline" className="text-xs">
                            {item.changed_by_role}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2 text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(item.changed_at)}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-xs">
                            {formatDistanceToNow(new Date(item.changed_at), {
                              addSuffix: true,
                              locale: fr
                            })}
                          </span>
                        </div>

                        {item.reason && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700 italic">
                            <strong>Raison :</strong> {item.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HistoriqueModal;
